import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../eco-icon/EcoIcon";
import {
  listStoreSlotsWithTokenApi,
  StoreSlotWithAvailability,
} from "../../../views/scheduling/schedulingApi";
import { getMyStoresPaginatedApi } from "../../../views/store/storeApi";
import {
  initialsOf,
  registrationStatusLabel,
  registrationStatusTone,
} from "../../../views/supervisor/registrationStatus";

import "./EvalSheet.css";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function buildIsoUtc(dayYmd: string, hhmm: string): string {
  return new Date(`${dayYmd}T${hhmm}:00`).toISOString();
}

function formatTimeFromIso(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function ymdFromIsoOrYmd(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const WEEKDAY_LONG = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

/** "12 a 18 de outubro" (ou "29 de setembro a 5 de outubro"). */
const weekTitle = (start: Date) => {
  const end = addDays(start, 6);
  return start.getMonth() === end.getMonth()
    ? `${start.getDate()} a ${end.getDate()} de ${MONTHS[end.getMonth()]}`
    : `${start.getDate()} de ${MONTHS[start.getMonth()]} a ${end.getDate()} de ${MONTHS[end.getMonth()]}`;
};

export type StoreForEvaluation = {
  id: string;
  nomeFantasia: string;
  endereco?: string | null;
  estado?: string | null;
};

export type EvaluationFreelancerCandidate = {
  registrationId: string;
  freelancerId: string;
  fullName: string;
  cpf: string;
  presenceStatus: string;
};

type Step = "STORE" | "SLOTS" | "FREELANCERS";

type Props = {
  isOpen: boolean;
  onClose: () => void;

  token: string;

  fetchFreelancersForEvaluation: (args: {
    token: string;
    storeId: string;
    dayYmd: string;
    slotId: string;
  }) => Promise<EvaluationFreelancerCandidate[]>;

  buildEvaluationPath: (args: { registrationId: string }) => string;

  onSelected?: (args: { registrationId: string }) => void;
};

const STORES_LIMIT = 8;

const STEPS: Array<{ key: Step; label: string }> = [
  { key: "STORE", label: "1. Loja" },
  { key: "SLOTS", label: "2. Horário" },
  { key: "FREELANCERS", label: "3. Freelancer" },
];

/**
 * "Avaliar freelancer" (redesign de 15/10/2026): loja → horário → freelancer,
 * com etapas visíveis. A escolha do freelancer abre a escolha do formulário.
 */
const SelectFreelancersForEvaluationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  token,
  fetchFreelancersForEvaluation,
  buildEvaluationPath,
  onSelected,
}) => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("STORE");

  // ===== Etapa 1: lojas =====
  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [stores, setStores] = useState<StoreForEvaluation[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreForEvaluation | null>(null);

  const fetchStores = useCallback(
    async (page: number) => {
      if (!token) return;

      try {
        setLoadingStores(true);
        const data = await getMyStoresPaginatedApi(token, page, STORES_LIMIT);
        const items = data.items ?? [];
        // "Mostrar mais" acrescenta; a página 1 recomeça a lista.
        setStores((prev) => (page === 1 ? items : [...prev, ...items]));
        setStoresTotal(data.total ?? 0);
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao buscar lojas.");
      } finally {
        setLoadingStores(false);
      }
    },
    [token],
  );

  // ===== Etapa 2: horários =====
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);

  const daysOfWeek = useMemo(() => {
    const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
    return labels.map((label, idx) => {
      const d = addDays(weekStart, idx);
      return { label, date: d, ymd: toYmd(d) };
    });
  }, [weekStart]);

  const [selectedDayYmd, setSelectedDayYmd] = useState<string>(() => toYmd(new Date()));
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [weekSlots, setWeekSlots] = useState<StoreSlotWithAvailability[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<StoreSlotWithAvailability | null>(null);

  const fetchWeekSlots = useCallback(async () => {
    if (!token) return;
    if (!selectedStore?.id) return;

    try {
      setLoadingSlots(true);

      const weekStartYmd = toYmd(weekStart);
      const weekEndYmd = toYmd(addDays(weekStart, 7));

      const from = buildIsoUtc(weekStartYmd, "00:00");
      const to = buildIsoUtc(weekEndYmd, "00:00");

      const slots = await listStoreSlotsWithTokenApi(selectedStore.id, token, {
        from,
        to,
        onlyAvailable: false,
        includeCanceled: false,
      });

      const sorted = [...(slots ?? [])].sort((a, b) => a.startAt.localeCompare(b.startAt));
      setWeekSlots(sorted);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao listar horários.");
    } finally {
      setLoadingSlots(false);
    }
  }, [token, selectedStore?.id, weekStart]);

  const slotsByDay = useMemo(() => {
    const map: Record<string, StoreSlotWithAvailability[]> = {};
    for (const s of weekSlots) {
      const dayYmd = ymdFromIsoOrYmd(s.day);
      if (!map[dayYmd]) map[dayYmd] = [];
      map[dayYmd].push(s);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.startAt.localeCompare(b.startAt));
    return map;
  }, [weekSlots]);

  const selectedDaySlots = useMemo(() => slotsByDay[selectedDayYmd] ?? [], [slotsByDay, selectedDayYmd]);

  // ===== Etapa 3: freelancers =====
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidates, setCandidates] = useState<EvaluationFreelancerCandidate[]>([]);

  const busy = loadingStores || loadingSlots || loadingCandidates;

  const goToCandidates = useCallback(
    async (slot: StoreSlotWithAvailability) => {
      if (!token) return;
      if (!selectedStore?.id) return;

      const dayYmd = ymdFromIsoOrYmd(slot.day);

      setSelectedSlot(slot);
      setStep("FREELANCERS");
      setCandidates([]);

      try {
        setLoadingCandidates(true);
        const list = await fetchFreelancersForEvaluation({
          token,
          storeId: selectedStore.id,
          dayYmd,
          slotId: slot.id,
        });
        setCandidates(list ?? []);
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao listar freelancers cadastrados.");
      } finally {
        setLoadingCandidates(false);
      }
    },
    [token, selectedStore?.id, fetchFreelancersForEvaluation],
  );

  const handlePickFreelancer = useCallback(
    (registrationId: string) => {
      if (!selectedStore?.id || !selectedSlot) return;

      const args = {
        storeId: selectedStore.id,
        dayYmd: ymdFromIsoOrYmd(selectedSlot.day),
        slotId: selectedSlot.id,
        freelancerId: "",
        registrationId,
      };

      onSelected?.(args);
      navigate(buildEvaluationPath(args));
      onClose();
    },
    [selectedStore?.id, selectedSlot, onSelected, navigate, buildEvaluationPath, onClose],
  );

  // ===== Comportamento do modal =====
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, busy]);

  useEffect(() => {
    if (!isOpen) return;

    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      onClose();
      return;
    }

    setStep("STORE");

    setStoresPage(1);
    setStoresTotal(0);
    setStores([]);
    setSelectedStore(null);

    setWeekAnchor(new Date());
    setSelectedDayYmd(toYmd(new Date()));
    setWeekSlots([]);
    setSelectedSlot(null);

    setCandidates([]);
    setLoadingCandidates(false);
  }, [isOpen, token, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    if (step !== "STORE") return;
    fetchStores(storesPage);
  }, [isOpen, step, storesPage, fetchStores]);

  useEffect(() => {
    if (!isOpen) return;
    if (step !== "SLOTS") return;
    fetchWeekSlots();
  }, [isOpen, step, fetchWeekSlots]);

  useEffect(() => {
    if (!isOpen) return;
    if (step !== "SLOTS") return;

    const inWeek = daysOfWeek.some((d) => d.ymd === selectedDayYmd);
    if (!inWeek) setSelectedDayYmd(daysOfWeek[0]?.ymd ?? toYmd(new Date()));
  }, [isOpen, step, daysOfWeek, selectedDayYmd]);

  const pickStore = (store: StoreForEvaluation) => {
    if (busy) return;
    setSelectedStore(store);
    setStep("SLOTS");
    setWeekAnchor(new Date());
    setSelectedDayYmd(toYmd(new Date()));
    setWeekSlots([]);
    setSelectedSlot(null);
    setCandidates([]);
  };

  const backToStores = () => {
    if (busy) return;
    setStep("STORE");
    setSelectedStore(null);
    setWeekSlots([]);
    setSelectedSlot(null);
    setCandidates([]);
  };

  const backToSlots = () => {
    if (busy) return;
    setStep("SLOTS");
    setSelectedSlot(null);
    setCandidates([]);
  };

  if (!isOpen) return null;

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const remainingStores = Math.max(0, storesTotal - stores.length);
  const selectedDay = daysOfWeek.find((d) => d.ymd === selectedDayYmd);

  return (
    <div className="evalSheet__overlay" onClick={() => !busy && onClose()} role="presentation">
      <div
        className="evalSheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="evalFreelancer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="evalSheet__grabber" aria-hidden="true">
          <span />
        </div>
        <header className="evalSheet__header">
          <div className="evalSheet__titles">
            <h2 id="evalFreelancer-title" className="evalSheet__title">
              Avaliar freelancer
            </h2>
          </div>
          <button type="button" className="evalSheet__close" onClick={onClose} aria-label="Fechar" data-modal-close>
            <EcoIcon name="x" size={20} />
          </button>
        </header>

        <ol className="evalSheet__steps" aria-label="Etapas">
          {STEPS.map((s, i) => (
            <li
              key={s.key}
              className={`evalSheet__step${
                i < stepIndex ? " evalSheet__step--done" : i === stepIndex ? " evalSheet__step--current" : ""
              }`}
              aria-current={i === stepIndex ? "step" : undefined}
            >
              {s.label}
            </li>
          ))}
        </ol>

        <div className="evalSheet__body">
          {/* ===== Etapa 1: loja ===== */}
          {step === "STORE" ? (
            <section className="evalSheet__group" aria-label="Lojas">
              <span className="evalSheet__label">Loja</span>
              {loadingStores && stores.length === 0 ? (
                <div className="evalSheet__empty">Carregando lojas...</div>
              ) : stores.length === 0 ? (
                <div className="evalSheet__empty">Nenhuma loja encontrada.</div>
              ) : (
                stores.map((store) => (
                  <button key={store.id} type="button" className="evalSheet__item" onClick={() => pickStore(store)}>
                    <span className="evalSheet__itemIcon" aria-hidden="true">
                      <EcoIcon name="store" size={20} />
                    </span>
                    <span className="evalSheet__optionText">
                      <span className="evalSheet__optionTitle">{store.nomeFantasia}</span>
                      <span className="evalSheet__optionSub">
                        {store.endereco || "Endereço não informado"}
                        {store.estado ? ` · ${store.estado}` : ""}
                      </span>
                    </span>
                    <EcoIcon name="chevronRight" size={18} className="evalSheet__chevron" />
                  </button>
                ))
              )}
              {remainingStores > 0 ? (
                <button
                  type="button"
                  className="evalSheet__more"
                  onClick={() => setStoresPage((p) => p + 1)}
                  disabled={loadingStores}
                >
                  {loadingStores ? "Carregando..." : `Mostrar mais ${remainingStores} ${remainingStores === 1 ? "loja" : "lojas"}`}
                </button>
              ) : null}
            </section>
          ) : null}

          {/* ===== Etapa 2: horário ===== */}
          {step === "SLOTS" ? (
            <>
              <div className="evalSheet__chosen">
                <span className="evalSheet__optionText">
                  <span className="evalSheet__optionTitle">{selectedStore?.nomeFantasia ?? "—"}</span>
                  <span className="evalSheet__optionSub">
                    {selectedStore?.endereco || "Endereço não informado"}
                    {selectedStore?.estado ? ` · ${selectedStore.estado}` : ""}
                  </span>
                </span>
                <button type="button" className="evalSheet__link" onClick={backToStores} disabled={busy}>
                  Trocar
                </button>
              </div>

              <div className="evalSheet__group">
                <div className="evalSheet__week">
                  <button
                    type="button"
                    className="evalSheet__iconBtn"
                    aria-label="Semana anterior"
                    onClick={() => setWeekAnchor((d) => addDays(d, -7))}
                    disabled={loadingSlots}
                  >
                    <EcoIcon name="chevronLeft" size={18} />
                  </button>
                  <span className="evalSheet__weekTitle">Semana de {weekTitle(weekStart)}</span>
                  <button
                    type="button"
                    className="evalSheet__iconBtn"
                    aria-label="Próxima semana"
                    onClick={() => setWeekAnchor((d) => addDays(d, 7))}
                    disabled={loadingSlots}
                  >
                    <EcoIcon name="chevronRight" size={18} />
                  </button>
                </div>
                <div className="evalSheet__days" role="group" aria-label="Dia">
                  {daysOfWeek.map((d) => {
                    const count = (slotsByDay[d.ymd] ?? []).length;
                    return (
                      <button
                        key={d.ymd}
                        type="button"
                        className="evalSheet__day"
                        aria-pressed={d.ymd === selectedDayYmd}
                        onClick={() => setSelectedDayYmd(d.ymd)}
                        disabled={loadingSlots || count === 0}
                        title={count === 0 ? "Sem horários neste dia" : undefined}
                      >
                        <span className="evalSheet__dayName">{d.label}</span>
                        <span className="evalSheet__dayNum">{d.date.getDate()}</span>
                        <span className="evalSheet__dayInfo">
                          {count === 0 ? "—" : `${count} ${count === 1 ? "turno" : "turnos"}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <section className="evalSheet__group" aria-label="Turnos do dia">
                <span className="evalSheet__label">
                  Turnos de {selectedDay ? WEEKDAY_LONG[selectedDay.date.getDay()] : ""},{" "}
                  {formatBrDate(selectedDayYmd).slice(0, 5)}
                </span>
                {loadingSlots ? (
                  <div className="evalSheet__empty">Carregando horários...</div>
                ) : selectedDaySlots.length === 0 ? (
                  <div className="evalSheet__empty">Selecione um dia que tenha horários.</div>
                ) : (
                  selectedDaySlots.map((s) => {
                    const endIsNextDay = s.endAt.slice(0, 10) !== s.startAt.slice(0, 10);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className="evalSheet__item"
                        onClick={() => goToCandidates(s)}
                        disabled={busy}
                      >
                        <span
                          className={`evalSheet__itemIcon${endIsNextDay ? " evalSheet__itemIcon--night" : ""}`}
                          aria-hidden="true"
                        >
                          <EcoIcon name={endIsNextDay ? "moon" : "clock"} size={20} />
                        </span>
                        <span className="evalSheet__optionText">
                          <span className="evalSheet__itemTitle">
                            {formatTimeFromIso(s.startAt)} – {formatTimeFromIso(s.endAt)}
                            {endIsNextDay ? " (+1)" : ""}
                          </span>
                          <span className="evalSheet__itemSub">
                            {s.occupied} {s.occupied === 1 ? "freelancer" : "freelancers"} · {s.available}{" "}
                            {s.available === 1 ? "vaga livre" : "vagas livres"}
                          </span>
                        </span>
                        {s.isFull ? <span className="evalSheet__pill">Lotado</span> : null}
                        <EcoIcon name="chevronRight" size={18} className="evalSheet__chevron" />
                      </button>
                    );
                  })
                )}
              </section>
            </>
          ) : null}

          {/* ===== Etapa 3: freelancer ===== */}
          {step === "FREELANCERS" ? (
            <>
              <dl className="evalSheet__context">
                <div className="evalSheet__contextWide">
                  <dt>Loja</dt>
                  <dd>{selectedStore?.nomeFantasia ?? "—"}</dd>
                </div>
                <div>
                  <dt>Dia</dt>
                  <dd>{selectedSlot ? formatBrDate(ymdFromIsoOrYmd(selectedSlot.day)) : "—"}</dd>
                </div>
                <div>
                  <dt>Turno</dt>
                  <dd>
                    {selectedSlot
                      ? `${formatTimeFromIso(selectedSlot.startAt)} – ${formatTimeFromIso(selectedSlot.endAt)}`
                      : "—"}
                  </dd>
                </div>
              </dl>

              <section className="evalSheet__group" aria-label="Freelancers do turno">
                <span className="evalSheet__label">Freelancers do turno</span>
                {loadingCandidates ? (
                  <div className="evalSheet__empty">Carregando freelancers...</div>
                ) : candidates.length === 0 ? (
                  <div className="evalSheet__empty">Nenhum freelancer encontrado para este dia e horário.</div>
                ) : (
                  candidates.map((c) => (
                    <button
                      key={c.registrationId ?? c.freelancerId}
                      type="button"
                      className="evalSheet__item"
                      onClick={() => handlePickFreelancer(c.registrationId)}
                      disabled={busy}
                    >
                      <span className="evalSheet__itemIcon evalSheet__itemIcon--round" aria-hidden="true">
                        {initialsOf(c.fullName)}
                      </span>
                      <span className="evalSheet__optionText">
                        <span className="evalSheet__optionTitle">{c.fullName}</span>
                        <span className="evalSheet__itemSub">
                          CPF {c.cpf}
                          <span className={`evalSheet__pill evalSheet__pill--${registrationStatusTone(c.presenceStatus)}`}>
                            {registrationStatusLabel(c.presenceStatus)}
                          </span>
                        </span>
                      </span>
                      <span className="evalSheet__action">Avaliar</span>
                    </button>
                  ))
                )}
              </section>
              <p className="evalSheet__hint">
                A presença mostrada é a registrada agora. Para corrigir, use Presença dos freelancers.
              </p>
            </>
          ) : null}
        </div>

        <footer className="evalSheet__footer">
          {step === "STORE" ? (
            <button type="button" className="ecoBtn" onClick={onClose} disabled={busy}>
              Fechar
            </button>
          ) : (
            <button type="button" className="ecoBtn" onClick={step === "SLOTS" ? backToStores : backToSlots} disabled={busy}>
              <EcoIcon name="arrowLeft" size={18} />
              {step === "SLOTS" ? "Voltar às lojas" : "Voltar ao horário"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default SelectFreelancersForEvaluationModal;
